import { useEffect, useState } from "react";
import { DialogProps } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import { getReportMonth, setReportMonth } from "../../utils/reportDialogState";

// Port of report_year_shell_c (IDD_REPORT_YEAR_SHELL): pick the year a
// YEAR-model report covers. Themed to match the Graph Channel Selection dialog.
export interface ReportYearDialogProps extends DialogProps {
  onOk?: (year: number) => void;
  onCancel?: () => void;
}

export function ReportYearDialog({
  open,
  onClose,
  onApply,
  onOk,
  onCancel,
}: ReportYearDialogProps) {
  const [year, setYear] = useState("");
  const [error, setError] = useState("");

  // Shares g_report_month_date with the month dialog in the C++.
  useEffect(() => {
    if (!open) return;
    setYear(String(getReportMonth().year));
    setError("");
  }, [open]);

  const handleOk = () => {
    const y = Number(year);
    if (!/^\d{4}$/.test(year) || y < 1901) {
      setError(getTigerResource("TIGER_REPORT_YEAR_INVALID"));
      return;
    }
    setError("");
    setReportMonth(1, y);
    onOk?.(y);
    onApply?.({ year: y });
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_REPORT_YEAR_TITLE");

  return (
    <div style={s.overlay}>
      <div
        style={{
          ...s.dialog,
          // Compact: the shared theme's 420px minWidth would otherwise win
          // over this width. The only input is a 4-digit year.
          width: 340,
          minWidth: 0,
          height: "auto",
          minHeight: 0,
          resize: "none",
          top: "calc(50vh - 110px)",
          left: "max(2.5vw, calc(50vw - 170px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={handleCancel} aria-label={getTigerResource("TIGER_RES_CLOSE")}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
            {getTigerResource("TIGER_REPORT_YEAR_LABEL")}
          </p>
          <div style={s.card}>
            {/* s.card is a column flex box, so size the field by width, not
                flex-basis (a basis here would fix its HEIGHT and let the
                input stretch to fill it). */}
            <div style={{ ...s.field, flex: "0 0 auto", width: 90 }}>
              <label style={s.fieldLabel} htmlFor="report-year">
                {getTigerResource("TIGER_REPORT_YEAR")}
              </label>
              <input
                id="report-year"
                style={{ ...s.input, flex: "0 0 auto", width: "100%", boxSizing: "border-box" }}
                type="text"
                inputMode="numeric"
                aria-label={getTigerResource("TIGER_REPORT_YEAR")}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                maxLength={4}
              />
            </div>
          </div>
          {error && (
            <div role="alert" style={{ color: "#b00020", fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>

        <div style={s.footer}>
          <button style={s.btnPrimary} onClick={handleOk}>
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={handleCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
